import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Eye, ChevronLeft, ChevronRight, ShoppingCart, FileText, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-500/20 text-yellow-500',
  processing: 'bg-blue-500/20 text-blue-500',
  shipped: 'bg-purple-500/20 text-purple-500',
  delivered: 'bg-[#78BE20]/20 text-[#78BE20]',
  cancelled: 'bg-red-500/20 text-red-500',
  draft: 'bg-zinc-700 text-zinc-300',
  sent: 'bg-blue-500/20 text-blue-400',
  paid: 'bg-[#78BE20]/20 text-[#78BE20]',
  unpaid: 'bg-yellow-500/20 text-yellow-500',
  overdue: 'bg-red-500/20 text-red-500',
  failed: 'bg-red-500/20 text-red-500',
  refunded: 'bg-orange-500/20 text-orange-500',
};

export default function AdminTransactions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchItems();
  }, [pagination.page, typeFilter, statusFilter]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', pagination.page.toString());
      if (typeFilter) params.set('type_filter', typeFilter);
      if (statusFilter) params.set('status_filter', statusFilter);
      const res = await axios.get(`${API}/admin/all-transactions?${params.toString()}`);
      setItems(res.data.items);
      setPagination({ page: res.data.page, pages: res.data.pages, total: res.data.total });
    } catch (err) {
      console.error('Error fetching transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  const openDetail = (item) => {
    if (item.type === 'order') {
      navigate(`/admin/orders?open=${item.id}`);
    } else {
      navigate(`/admin/invoices?open=${item.id}`);
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-transactions">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-black text-white uppercase tracking-tight">
            Orders & Invoices
          </h1>
          <div className="flex gap-2">
            <Select value={typeFilter || "all"} onValueChange={(v) => { setTypeFilter(v === 'all' ? '' : v); setPagination(p => ({ ...p, page: 1 })); }}>
              <SelectTrigger className="w-36 bg-zinc-800 border-zinc-700">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="order">Orders</SelectItem>
                <SelectItem value="invoice">Invoices</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter || "all"} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPagination(p => ({ ...p, page: 1 })); }}>
              <SelectTrigger className="w-40 bg-zinc-800 border-zinc-700">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="shipped">Shipped</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Summary */}
        <div className="flex gap-2 mb-4 text-xs text-zinc-500">
          <span>{pagination.total} total</span>
        </div>

        {/* Table */}
        <div className="bg-[#18181b] border border-zinc-800 rounded-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-zinc-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Number</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Payment</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Total</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Xero</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Date</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {loading ? (
                [...Array(8)].map((_, i) => (
                  <tr key={i}><td colSpan={9} className="px-4 py-4"><div className="h-8 bg-zinc-800 rounded animate-pulse" /></td></tr>
                ))
              ) : items.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-zinc-500">No transactions found</td></tr>
              ) : (
                items.map(item => (
                  <tr key={`${item.type}-${item.id}`} className="hover:bg-zinc-900/50">
                    <td className="px-4 py-3">
                      {item.type === 'order' ? (
                        <span className="inline-flex items-center gap-1 text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">
                          <ShoppingCart className="h-3 w-3" /> Order
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">
                          <FileText className="h-3 w-3" /> Invoice
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-white font-mono text-sm">{item.number}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-white text-sm">{item.customer_name}</p>
                      <p className="text-xs text-zinc-500">{item.customer_email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={statusColors[item.status] || 'bg-zinc-700 text-zinc-400'}>{item.status}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={statusColors[item.payment_status] || 'bg-zinc-700 text-zinc-400'}>{item.payment_status}</Badge>
                    </td>
                    <td className="px-4 py-3 text-[#78BE20] font-mono font-bold text-sm">
                      ${item.total?.toFixed(2)}
                    </td>
                    <td className="px-4 py-3">
                      {item.xero_invoice_id ? (
                        <span className="text-xs text-[#13B5EA]" title={item.xero_invoice_id}>Synced</span>
                      ) : (
                        <span className="text-xs text-zinc-600">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-zinc-400 text-sm">
                      {item.created_at ? new Date(item.created_at).toLocaleDateString('en-AU') : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="icon" onClick={() => openDetail(item)} className="text-zinc-400 hover:text-white">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <Button variant="outline" size="icon" disabled={pagination.page <= 1} onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} className="border-zinc-700">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-zinc-400 px-4">Page {pagination.page} of {pagination.pages}</span>
            <Button variant="outline" size="icon" disabled={pagination.page >= pagination.pages} onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} className="border-zinc-700">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
